import React, { useRef, useEffect, useState } from 'react';
import { ThinkingIndicator } from '../ui';
import MessageContent from './MessageContent';
import UpgradePrompt from './RateLimitMessage';
import Advertisement from './Advertisement';
import AdBlockerDetection from './AdBlockerDetection';
import { Message } from '../../types/message';
import { useSubscription } from '../../contexts/SubscriptionContext';

interface ChatInterfaceProps {
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  isRateLimited: boolean;
  onUpgradeClick: () => void;
}

// Helper function to determine if an ad should be shown based on message index and ad frequency
const shouldShowAd = (index: number, adFrequency: number): boolean => {
  // If adFrequency is 0, never show ads
  if (adFrequency === 0) return false;
  
  // Otherwise, show an ad every 'adFrequency' user messages
  // We count user messages (not total messages) and show an ad when the count is divisible by adFrequency
  // Add 1 to make it 1-based (so adFrequency=1 shows an ad after every message)
  return ((index + 1) % adFrequency) === 0;
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  messages, 
  isLoading,
  isStreaming,
  isRateLimited, 
  onUpgradeClick 
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { tier, adFrequency } = useSubscription();
  
  // Count user messages to determine when to show ads
  const userMessageIndices = messages
    .map((message, index) => message.role === 'user' ? index : -1)
    .filter(index => index !== -1);
  
  // PHASE 1 SECURITY: Artificial delay removed - server now enforces all delays
  // The server applies 8-12 second delays for free tier users that cannot be bypassed
  
  // Scroll to bottom when messages change
  useEffect(() => {
    // Use a small delay to ensure all content (including ads) has rendered
    const scrollTimer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    return () => clearTimeout(scrollTimer);
  }, [messages]);

  return (
    <div className="absolute inset-0 overflow-y-auto">
      <div className="p-4 space-y-4 pb-32">
        {messages.map((message, index) => (
          <React.Fragment key={message.id}>
            <div 
              className={"flex " + (message.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div 
                className={
                  "max-w-[85%] rounded-lg p-4 " + (
                    message.role === 'user' 
                      ? 'bg-emerald-600 text-slate-100 shadow-lg' 
                      : 'bg-slate-700 text-slate-100 shadow-lg'
                  )
                }
              >
                <MessageContent 
                  content={message.content} 
                  isUser={message.role === 'user'} 
                />
              </div>
            </div>
            
            {/* Show advertisement after user message and before AI responses based on adFrequency */}
            {tier === 'free' && message.role === 'user' && index < messages.length - 1 && messages[index + 1].role === 'assistant' && (
              <>
                {/* Find the position of this user message in the list of user messages */}
                {(() => {
                  const userMessagePosition = userMessageIndices.indexOf(index);
                  // Only show ad if it matches the frequency setting
                  return shouldShowAd(userMessagePosition, adFrequency) ? (
                    <Advertisement 
                      key={`ad-${message.id}`} 
                      onUpgradeClick={onUpgradeClick} 
                    />
                  ) : null;
                })()}
              </>
            )}
          </React.Fragment>
        ))}
        
        {/* Show thinking indicator when loading */}
        {isLoading && !isStreaming ? (
          <ThinkingIndicator />
        ) : null}
        
        {isRateLimited && (
          <UpgradePrompt
            onUpgradeClick={onUpgradeClick}
          />
        )}
        
        {/* Ad blocker detection for free tier */}
        {tier === 'free' && <AdBlockerDetection onUpgradeClick={onUpgradeClick} />}
        
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatInterface;
